import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Observable, BehaviorSubject } from "rxjs";
import { first } from "rxjs/operators";
import gql from "graphql-tag";
import { cloneDeep, get as _get } from "lodash";

import { environment } from "@environments/environment";
import { AuthService } from "@examsoft/es-login";
import { AppSyncService } from "../app-sync/app-sync.service";

import Poll from "@shared/types/poll.type";
import PollStatus from "@shared/types/poll-status.type";
import Question from "@shared/types/question.type";
import Response from "@shared/types/response.type";
import * as subscriptions from "./graphql/subscription";
import * as queries from "./graphql/query";
import * as mutations from "./graphql/mutation";

@Injectable()
export class PollDataService {
  client;
  pollHistory = new BehaviorSubject<string>(null);
  gatewayApiUrl = environment.urls.gatewayApiUrl;

  constructor(
    public authService: AuthService,
    private appsync: AppSyncService,
    private http: HttpClient
  ) {
    this.client = this.appsync.client;
  }

  async getPolls(esId): Promise<Poll[]> {
    await this.client.hydrated();
    return this.client
      .query({
        query: gql(queries.getPolls),
        variables: {
          esId: esId,
        },
        fetchPolicy: "network-only",
      })
      .then((pollResults) => {
        return pollResults.data.getPolls.map((poll) => {
          return {
            id: poll.sk,
            courseId: poll.courseId,
            title: poll.title,
            portalPid: poll.portalPid,
            numQuestions: poll.numQuestions,
            numStudents: poll.numStudents,
            scheduleDate: poll.scheduleDate,
            status: poll.LSI1SK,
          };
        });
      });
  }

  async getStudentPolls(esId): Promise<Poll[]> {
    await this.client.hydrated();
    return this.client
      .query({
        query: gql(queries.getStudentPolls),
        variables: {
          studentId: esId,
          pollStatus: "active",
        },
        fetchPolicy: "network-only",
      })
      .then((pollResults) => {
        return pollResults.data.getStudentPolls.map((poll) => {
          return {
            id: poll.sk,
            portalPid: poll.portalPid,
            title: poll.title,
          };
        });
      });
  }

  async getPoll(pollId): Promise<Poll> {
    await this.client.hydrated();
    return this.client
      .query({
        query: gql(queries.getPoll),
        variables: {
          pollId,
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const poll = data.getPoll;
        return {
          id: poll.pk,
          title: poll.title,
          portalPid: poll.portalPid,
          numQuestions: poll.numQuestions,
          numStudents: poll.numStudents,
          scheduleDate: poll.scheduleDate,
          status: poll.LSI1SK,
          lastAccessed: poll.lastAccessed,
          lastInstructor: poll.lastInstructor,
          currentQuestion: parseInt(poll.currentQuestion, 10),
        };
      });
  }

  getPollUpdates(pollId): Observable<Poll> {
    return this.client
      .subscribe({
        query: gql(subscriptions.onUpdatePoll),
        variables: { pk: pollId },
        fetchPolicy: "network-only",
      })
      .map((pollResults) => {
        const poll = pollResults.data.onUpdatePoll;
        return {
          id: poll.pk,
          currentQuestion: parseInt(poll.currentQuestion, 10),
          status: poll.LSI1SK,
          lastAccessed: poll.lastAccessed,
        };
      });
  }

  getActivePollUpdates(studentId): Observable<any> {
    return this.client
      .subscribe({
        query: gql(subscriptions.onActivateUserPoll),
        variables: { pk: studentId },
        fetchPolicy: "network-only",
      })
      .map((results) => {
        const activeUserPoll = results.data.onActivateUserPoll;
        return {
          studentId: activeUserPoll.pk,
          pollId: activeUserPoll.sk,
          pollStatus: activeUserPoll.LSI1SK,
        };
      });
  }

  completeUserPoll(esId: string, pollId: string): Promise<any> {
    return this.client
      .mutate({
        mutation: gql(mutations.completeUserPoll),
        variables: {
          esId,
          pollId,
        },
      })
      .then((pollResults) => {
        let data = pollResults.data.completeUserPoll;
        return {
          esId: data.pk,
          pollId: data.sk,
          status: data.LSI1SK,
        };
      });
  }

  async updatePollInput(
    pk: string,
    sk: string,
    currentQuestion: number,
    status: PollStatus,
    lastInstructor: string
  ): Promise<any> {
    await this.client.hydrated();
    return this.client
      .mutate({
        mutation: gql(mutations.updatePollInput),
        variables: {
          pk,
          sk,
          currentQuestion: this.padQuestionNumber(currentQuestion),
          LSI1SK: status,
          lastInstructor,
        },
        optimisticResponse: () => {
          return {
            updatePollInput: {
              __typename: "Poll",
              sk,
              pk,
              currentQuestion: this.padQuestionNumber(currentQuestion),
              LSI1SK: status,
              lastInstructor,
              lastAccessed: 0,
            },
          };
        },
        update: (cache, { data: { updatePollInput } }) => {
          const query = gql(queries.getPoll);
          const data = cache.readQuery({ query, variables: { pk } });
          data.getPoll = {
            ...cloneDeep(data.getPoll),
            ...cloneDeep(updatePollInput),
          };
          cache.writeQuery({ query, data });
        },
      })
      .then((pollResults) => {
        return {
          id: pollResults.data.updatePollInput.pk,
          currentQuestion: parseInt(
            pollResults.data.updatePollInput.currentQuestion,
            10
          ),
          status: pollResults.data.updatePollInput.LSI1SK,
        };
      });
  }

  async updatePollDetails(
    pollId: string,
    currentQuestion: number,
    status: PollStatus,
    esId: string
  ): Promise<any> {
    await this.client.hydrated();
    return this.client
      .mutate({
        mutation: gql(mutations.updatePoll),
        variables: {
          pollId,
          currentQuestion: this.padQuestionNumber(currentQuestion),
          status,
          esId,
        },
        optimisticResponse: () => {
          return {
            updatePoll: {
              __typename: "Poll",
              pk: pollId,
              currentQuestion: this.padQuestionNumber(currentQuestion),
              LSI1SK: status,
              lastInstructor: esId,
              lastAccessed: 0,
            },
          };
        },
        update: (cache, { data: { updatePoll } }) => {
          const query = gql(queries.getPoll);
          const data = cache.readQuery({ query, variables: { pollId } });
          data.getPoll = {
            ...cloneDeep(data.getPoll),
            ...cloneDeep(updatePoll),
          };
          cache.writeQuery({ query, data });
        },
      })
      .then((pollResults) => {
        return {
          id: pollResults.data.updatePoll.pk,
          currentQuestion: parseInt(
            pollResults.data.updatePoll.currentQuestion,
            10
          ),
          status: pollResults.data.updatePoll.LSI1SK,
        };
      });
  }

  updateLastAccessed(pk: string, sk: string): Promise<any> {
    return this.client
      .mutate({
        mutation: gql(mutations.updateLastAccessed),
        variables: {
          pk: pk,
          sk: sk,
        },
      })
      .then((pollResults) => {
        return _get(pollResults, "data.updateLastAccessed", {
          pk: null,
          sk: null,
          lastAccessed: null,
        });
      });
  }

  activateUserPolls(pollId) {
    return this.http
      .post(
        `${this.gatewayApiUrl}/examnow-appsync-mutations/${pollId}`,
        { action: "activate", payload: { pollId } },
        this.getHttpOptions()
      )
      .toPromise();
  }

  completePoll(pollId: string): any {
    return Promise.all([
      this.http
        .post(
          //update poll user records to complete
          `${this.gatewayApiUrl}/examnow-appsync-mutations/${pollId}`,
          { action: "complete", payload: { pollId } }
        )
        .toPromise(),
      this.http
        .post(
          //update main poll record to complete
          `${this.gatewayApiUrl}/AudienceResponseAnswerUpload/${pollId}`,
          { pollId: pollId }
        )
        .toPromise(),
    ]).then(([activations, completed]) => {});
  }

  async getPollStudents(pollId) {
    await this.client.hydrated();
    return this.client
      .query({
        query: gql(queries.getPollStudents),
        variables: {
          pollId,
        },
        fetchPolicy: "network-only",
      })
      .then((students) => {
        return students.data.getPollStudents.map((student) => {
          return {
            studentId: student.pk,
            pollId: student.sk,
          };
        });
      });
  }

  async getPollQuestions(pollId): Promise<Question[]> {
    await this.client.hydrated();
    return this.client
      .query({
        query: gql(queries.getPollQuestions),
        variables: {
          pollId,
        },
        fetchPolicy: "network-only",
      })
      .then((response) => {
        const pollQuestions = response.data.getPollQuestions;
        return pollQuestions.map((q) => {
          return {
            id: q.sk,
            stem: q.stem,
            type: q.type,
            questionStatus: q.questionStatus,
            choices: q.choices,
            enableAllThatApply: q.enableAllThatApply,
            mcAnswer: q.mcAnswer,
            tfAnswer: q.tfAnswer,
            fitbAnswer: q.fitbAnswer,
          };
        });
      });
  }

  async getPollQuestion(pollId, currentQuestion): Promise<Question> {
    await this.client.hydrated();
    return this.client
      .query({
        query: gql(queries.getPollQuestion),
        variables: {
          pollId,
          currentQuestion: this.padQuestionNumber(currentQuestion),
        },
        fetchPolicy: "network-only",
      })
      .then((response) => {
        const pollQuestion = response.data.getPollQuestion;
        return {
          id: pollQuestion.sk,
          stem: pollQuestion.stem,
          type: pollQuestion.type,
          questionStatus: pollQuestion.questionStatus,
          choices: pollQuestion.choices,
          enableAllThatApply: pollQuestion.enableAllThatApply,
          fitbAnswer: pollQuestion.fitbAnswer,
          mcAnswer: pollQuestion.mcAnswer,
          tfAnswer: pollQuestion.tfAnswer,
        };
      });
  }

  getQuestionStatusUpdates(pollId, questionId): Observable<any> {
    return this.client
      .subscribe({
        query: gql(subscriptions.onUpdateQuestionStatus),
        variables: {
          pk: pollId,
          sk: this.padQuestionNumber(questionId),
        },
        fetchPolicy: "network-only",
      })
      .map((questionResults) => {
        const q = questionResults.data.onUpdateQuestionStatus;
        return {
          id: q.sk,
          questionStatus: q.questionStatus,
        };
      });
  }

  updateQuestionStatus(
    pollId: string,
    currentQuestion: number,
    questionStatus: string
  ): Promise<any> {
    return this.client
      .mutate({
        mutation: gql(mutations.updateQuestionStatus),
        variables: {
          pollId,
          questionId: this.padQuestionNumber(currentQuestion),
          questionStatus,
        },
        optimisticResponse: () => {
          return {
            updateQuestionStatus: {
              __typename: "Question",
              pk: pollId,
              sk: this.padQuestionNumber(currentQuestion),
              questionStatus,
            },
          };
        },
        update: (cache, { data: { updateQuestionStatus } }) => {
          const query = gql(queries.getPollQuestions);
          const data = cache.readQuery({ query, variables: { pollId } });
          data.getPollQuestions.forEach((question) => {
            if (question.sk === this.padQuestionNumber(currentQuestion)) {
              question.questionStatus = questionStatus;
            }
          });
          cache.writeQuery({ query, data });
        },
      })
      .then(({ data: { updateQuestionStatus } }) => {
        return {
          id: updateQuestionStatus.sk,
          questionStatus: updateQuestionStatus.questionStatus,
        };
      });
  }

  getPollResponses(pollId: string, responseId = null): Promise<Response[]> {
    return this.client
      .query({
        query: gql(queries.getPollResponses),
        variables: {
          pollId,
          responseId,
        },
        fetchPolicy: "network-only",
      })
      .then((responseResults) => {
        return responseResults.data.getPollResponses.map((response) => {
          return {
            userId: response.pk,
            responseId: response.GSI2SK || response.sk,
            pollId: response.GSI2PK,
            response: response.response,
          };
        });
      });
  }

  getStudentResponses(esId: string, responseId = null): Promise<Response[]> {
    return this.client
      .query({
        query: gql(queries.getStudentResponses),
        variables: {
          esId,
          responseId,
        },
        fetchPolicy: "network-only",
      })
      .then((responseResults) => {
        return responseResults.data.getStudentResponses.map((response) => {
          return {
            userId: response.pk,
            responseId: response.GSI2SK || response.sk,
            pollId: response.GSI2PK,
            response: response.response,
          };
        });
      });
  }

  getResponseUpdates(pollId): Observable<Response> {
    return this.client
      .subscribe({
        query: gql(subscriptions.onAddResponseToPoll),
        variables: {
          GSI2PK: pollId,
        },
        fetchPolicy: "network-only",
      })
      .map((responseResult) => {
        const response = responseResult.data.onAddResponseToPoll;
        return {
          userId: response.pk,
          responseId: response.GSI2SK || response.sk,
          pollId: response.GSI2PK,
          response: response.response,
        };
      });
  }

  getResponseDeleteUpdates(esId, responseId): Observable<Response> {
    return this.client
      .subscribe({
        query: gql(subscriptions.onDeletePollResponse),
        variables: {
          pk: esId,
          sk: responseId,
        },
        fetchPolicy: "network-only",
      })
      .map((responseResults) => {
        const response = responseResults.data.onDeletePollResponse;
        return {
          userId: response.pk,
          responseId: response.GSI2SK || response.sk,
          pollId: response.GSI2PK,
          response: response.response,
        };
      });
  }

  deletePollResponses(pollId, responseId = null) {
    return this.http
      .post(`${this.gatewayApiUrl}/examnow-appsync-mutations/${pollId}`, {
        action: "deleteResponses",
        payload: { pollId, responseId },
      })
      .toPromise();
  }

  addResponseToPoll(studentId, questionId, pollId, response) {
    const responseId = `response#${pollId}#${this.padQuestionNumber(
      questionId
    )}`;
    let data = {
      pk: studentId,
      sk: responseId,
      GSI2PK: pollId,
      GSI2SK: responseId,
      response: {
        ...response,
      },
    };
    return this.client
      .mutate({
        mutation: gql(mutations.addResponseToPoll),
        variables: data,
        optimisticResponse: () => {
          return {
            addResponseToPoll: {
              __typename: "Response",
              pk: studentId,
              sk: responseId,
              GSI2PK: pollId,
              GSI2SK: responseId,
              response: {
                __typename: "ResponseDetails",
                choiceIndexes: [],
                text: null,
                shortAnswerBlanks: {
                  __typename: "ShortAnswerBlank",
                  blankUID: null,
                  userAnswer: null,
                },
                ...cloneDeep(response),
              },
            },
          };
        },
        update: (cache, { data: { addResponseToPoll } }) => {
          const query = gql(queries.getStudentResponses);
          const data = cache.readQuery({
            query,
            variables: { esId: studentId, responseId },
          });
          data.getStudentResponses = [addResponseToPoll];
          cache.writeQuery({ query, data });
        },
      })
      .then((responseResults) => {
        const response = responseResults.data.addResponseToPoll;
        return {
          userId: response.pk,
          responseId: response.GSI2SK || response.sk,
          pollId: response.GSI2PK,
          response: response.response,
        };
      });
  }

  padQuestionNumber(currentQuestion): string {
    let currentQuestionString: string = currentQuestion.toString();
    while (currentQuestionString.length < 3) {
      currentQuestionString = "0" + currentQuestionString;
    }
    return currentQuestionString;
  }

  private getHttpOptions(): { headers: HttpHeaders } {
    // TODO: Add test for cognito login?
    return {
      headers: new HttpHeaders({
        Authorization: this.authService.getJwt(),
        "Content-Type": "application/json",
      }),
    };
  }
}
